import React from "react";

import { AssetTypes } from "common/utils/types";
import { CommonLinearProgress } from "common/components/CommonLinearProgress";
import { formatDateDifference } from "common/utils/utility";
import { Property } from "common/store/property/types";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  makeStyles,
} from "@material-ui/core";

interface Props {
  property: Property;
  selected: boolean;
}

const paperStyle = makeStyles({
  root: {
    height: "522px",
    "&:first-child": {
      borderBottomLeftRadius: "0px",
      height: "100%",
      borderTopLeftRadius: "0px",
    },
  },
});

const paperNoSelectedStyle = makeStyles({
  root: {
    height: "480px",
    "&:first-child": {
      borderBottomLeftRadius: "0px",
      height: "100%",
      borderTopLeftRadius: "0px",
    },
  },
});

const paperNoSelectedLoanStyle = makeStyles({
  root: {
    "&:first-child": {
      borderBottomLeftRadius: "0px",
      height: "100%",
      borderTopLeftRadius: "0px",
    },
  },
});

const paperSelectedLoanStyle = makeStyles({
  root: {
    "&:first-child": {
      borderBottomLeftRadius: "0px",
      height: "100%",
      borderTopLeftRadius: "0px",
    },
  },
});

const itemTextStyle = makeStyles({
  root: {
    fontSize: 18,
    fontWeight: 500,
    color: "#696F74",
    marginBottom: 12,
  },
});

const itemValueStyle = makeStyles({
  root: {
    color: "#4A4A4A",
    fontSize: 18,
    fontWeight: 500,
  },
});

export const PropertyInfo: React.FC<Props> = ({ property, selected }) => {
  const history = useHistory();
  const itemText = itemTextStyle();
  const itemValue = itemValueStyle();
  const paper = paperStyle();
  const paperNo = paperNoSelectedStyle();
  const paperNoLoan = paperNoSelectedLoanStyle();
  const paperLoan = paperSelectedLoanStyle();

  const nFormatter = (num: number) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1).replace(/\.0$/, "") + "G";
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "m";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    }
    return num;
  };

  const { t } = useTranslation();

  const { id, cap, token, total_sold } = property;

  const duration = formatDateDifference(property.end, property.start);

  return (
    <Paper
      classes={
        selected && token.asset_type !== AssetTypes.LOAN
          ? paper
          : selected && token.asset_type === AssetTypes.LOAN
          ? paperLoan
          : token.asset_type === AssetTypes.LOAN
          ? paperNoLoan
          : paperNo
      }
    >
      <Box
        paddingX="10%"
        paddingTop="84px"
        paddingBottom="100px"
        boxSizing="border-box"
        textAlign="center"
      >
        <Typography
          className={"property-name" + (selected ? "--selected" : "")}
        >
          {token.name}
        </Typography>
        <Box
          paddingTop="20px"
          marginTop="20px"
          boxSizing="border-box"
          textAlign="initial"
        >
          <Grid container>
            {property.cost ? (
              <>
                <Grid item xs={6}>
                  <Typography classes={itemText}>{t("Cost")}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right" classes={itemValue}>
                    €{nFormatter(Number(property.cost))}
                  </Typography>
                </Grid>
              </>
            ) : null}

            <Grid item xs={6}>
              <Typography classes={itemText}>
                {t("Time till repayed")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" classes={itemValue}>
                {duration}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography classes={itemText}>{t("Returns p.a.")}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" classes={itemValue}>
                €{nFormatter(1232.0)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography classes={itemText}>
                {t("Shares Available")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" classes={itemValue}>
                {nFormatter(Number(cap) - Number(total_sold))}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box paddingY="20px" boxSizing="border-box">
          <CommonLinearProgress
            label={t("Funded")}
            value={(total_sold * 100) / cap}
          />
        </Box>
        <Button
          className="primary"
          variant="contained"
          onClick={() => history.push(`/offering/${id}`)}
        >
          {t("Explore project")}
        </Button>
      </Box>
    </Paper>
  );
};
